/**
 * Coluna especial de "arquivo/lixeira": recebe os status órfãos quando uma
 * coluna é removida, para que nenhum status seja perdido. Não aparece no quadro
 * (board) e fica isenta da validação de "coluna sem itens".
 */
export const ARCHIVE_COLUMN_ID = '__archive__';
export const ARCHIVE_COLUMN_NAME = 'Arquivados';

export function isArchiveColumn(col) {
    return !!col && (col.is_archive === true || col.id === ARCHIVE_COLUMN_ID);
}

export function buildArchiveColumn(items = []) {
    return { id: ARCHIVE_COLUMN_ID, name: ARCHIVE_COLUMN_NAME, is_archive: true, items };
}

/**
 * A coluna nova entra no INÍCIO, e "Arquivados" continua no fim.
 *
 * ## O pedido
 *
 * "Nova coluna deve aparecer no início (hoje a coluna padrão 'Arquivados' fica
 * antes, exigindo reorganização manual)." -- Gisele, bloco "Criação e
 * configuração do Kanban".
 *
 * ## O que acontecia
 *
 * "Adicionar coluna" fazia `[...colunas, nova]`, e a de arquivo já estava no
 * fim. A nova nascia DEPOIS dela -- fora da ordem e, num kanban com muitas
 * colunas, fora da tela, porque a fila rola na horizontal.
 *
 * O arquivo é posto no fim aqui também, e não só preservado: se ele tiver
 * parado no meio por qualquer caminho, esta é a hora de consertar. Ele não é
 * uma etapa do fluxo, é o lugar onde status órfãos esperam -- e aparecer
 * entre duas etapas sugere que é uma delas.
 *
 * @param {Array} colunas
 * @param {object} nova
 * @returns {Array} uma lista nova (não altera a recebida)
 */
export function adicionarColunaNoInicio(colunas, nova) {
    const atuais = Array.isArray(colunas) ? colunas : [];
    const arquivo = atuais.filter(isArchiveColumn);
    const etapas = atuais.filter((c) => !isArchiveColumn(c));

    return [nova, ...etapas, ...arquivo];
}

export function kanbanItemKey(it) {
    if (!it || it.status_id == null) return '';
    return `${it.status_id}-${it.substatus_id ?? 'n'}`;
}

/** Item padrão do status na coluna da etapa (ticket sem justificativa). */
export function buildStatusWithoutJustificationItem(statusId) {
    return { status_id: statusId, substatus_id: null };
}

/**
 * Todas as combinações possíveis no cadastro atual.
 * Justificativas entram aqui; o status sem justificativa conta como atribuído se estiver em alguma coluna.
 */
export function buildKanbanItemsFromStatuses(ticketStatuses) {
    const items = [];
    (ticketStatuses || []).forEach((s) => {
        const subs = Array.isArray(s.substatuses) ? s.substatuses : [];
        items.push(buildStatusWithoutJustificationItem(s.id));
        subs.forEach((sub) => {
            items.push({ status_id: s.id, substatus_id: sub.id });
        });
    });
    return items;
}

/** Itens que vão na coluna predeterminada ao criar o filtro: só status sem justificativa por etapa. */
export function buildPredeterminedKanbanColumnItems(ticketStatuses) {
    const items = [];
    (ticketStatuses || []).forEach((s) => {
        items.push(buildStatusWithoutJustificationItem(s.id));
    });
    return items;
}

export function collectAssignedKanbanKeys(kanbanColumns) {
    const keys = new Set();
    (kanbanColumns || []).forEach((col) => {
        (col.items || []).forEach((it) => {
            const k = kanbanItemKey(it);
            if (k) keys.add(k);
        });
    });
    return keys;
}

/**
 * Itens (status e justificativas) ainda não colocados em nenhuma coluna — ex.:
 * justificativa criada depois do filtro, ou status que ficou órfão ao remover
 * uma coluna. Inclui o status sem justificativa quando ele não está presente em
 * NENHUMA coluna, garantindo que possa ser reutilizado (não desaparece).
 */
export function getUnassignedKanbanItems(ticketStatuses, kanbanColumns) {
    const all = buildKanbanItemsFromStatuses(ticketStatuses);
    const assigned = collectAssignedKanbanKeys(kanbanColumns);
    return all.filter((it) => {
        const key = kanbanItemKey(it);
        if (!key || assigned.has(key)) return false;
        return true;
    });
}

export function buildInitialKanbanColumnsFromStatuses(ticketStatuses) {
    const byStage = {};
    (ticketStatuses || []).forEach((s) => {
        const key = s.stage?.name || 'Outros';
        if (!byStage[key]) byStage[key] = [];
        byStage[key].push(buildStatusWithoutJustificationItem(s.id));
    });
    return Object.entries(byStage).map(([name, items], idx) => ({
        id: Date.now() + idx,
        name,
        items: items || [],
    }));
}

/** Garante entrada "sem justificativa" para cada status presente na coluna. */
export function enrichKanbanColumnItems(items) {
    const list = Array.isArray(items) ? [...items] : [];
    const statusIds = [...new Set(list.map((it) => it.status_id).filter((id) => id != null))];
    const missingNull = statusIds
        .filter(
            (statusId) =>
                !list.some((it) => it.status_id === statusId && (it.substatus_id == null || it.substatus_id === ''))
        )
        .map((statusId) => buildStatusWithoutJustificationItem(statusId));
    return [...missingNull, ...list];
}

export function parseKanbanColumnsFromFilter(rawKc, ticketStatuses) {
    if (!Array.isArray(rawKc)) return [];
    return rawKc.map((c, idx) => {
        if (typeof c !== 'object' || c?.id == null) {
            return { id: c, name: `Coluna ${idx + 1}`, items: [] };
        }
        let items = Array.isArray(c.items) ? [...c.items] : [];
        const fromStatusIds = Array.isArray(c.status_ids) ? c.status_ids : [];
        if (items.length === 0 && fromStatusIds.length > 0) {
            items = fromStatusIds.map((sid) => buildStatusWithoutJustificationItem(sid));
        }
        // Mantém exatamente os itens salvos. Não injetamos o status "sem
        // justificativa" (pai) automaticamente: se a coluna tem só um substatus,
        // ela deve ficar só com o substatus. O status pai é um item arrastável
        // próprio (fica nos itens disponíveis) e só entra numa coluna se o
        // usuário arrastá-lo explicitamente.
        const col = { id: c.id, name: c.name || `Coluna ${idx + 1}`, items };
        if (isArchiveColumn(c)) col.is_archive = true;
        return col;
    });
}
